/**
 * api.ts — TanStack Query hooks for the schedule feature.
 *
 * Endpoints:
 *   GET  /schedule?from=&to=&branch=     → ScheduleRow[]
 *   GET  /ekip/confirms/{orderId}        → accept/complete stamps of one order
 *   GET  /schedule/postponed             → settings KV (postponed orders)
 *   PUT  /schedule/postponed             → save postponed
 *   POST /schedule/postponed/orders/{id} → postpone order + clear schedule
 *   POST /schedule/postponed/orders/{id}/resume → restore schedule
 *
 * Polling every 30s replaces Appwrite Realtime.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";
import { scheduleKeys } from "./queryKeys";

export type ScheduleRow = components["schemas"]["ScheduleRow"];

export type ScheduleParams = {
  from: string; // YYYY-MM-DD
  to: string; // YYYY-MM-DD
  branch?: string;
};

export function useSchedule({ from, to, branch }: ScheduleParams) {
  return useQuery({
    queryKey: scheduleKeys.range({ from, to, branch }),
    queryFn: async () => {
      const { data, error } = await api.GET("/schedule", {
        params: {
          query: {
            from,
            to,
            ...(branch ? { branch } : {}),
          },
        },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được lịch"));
      return data;
    },
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

// ─── ekip confirms ───────────────────────────────────────────────────────

export type EkipConfirmMap = components["schemas"]["EkipConfirmMap"];

/** Key suffix marking "member took the shift"; a bare staff id means "shot". */
const ACCEPT_SUFFIX = "_accept";

/**
 * Accept/complete stamps for every crew member on one order.
 *
 * `GET /ekip/schedule` only stamps the calling user, so the detail panel —
 * which draws one chip per assigned member — needs this second read.
 */
export function useEkipConfirms(orderId: string | null) {
  return useQuery({
    queryKey: ["ekip", "confirms", orderId ?? ""],
    enabled: !!orderId,
    queryFn: async () => {
      const { data, error } = await api.GET("/ekip/confirms/{orderId}", {
        params: { path: { orderId: orderId! } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được trạng thái nhận ca"));
      return data;
    },
    staleTime: 15_000,
  });
}

export type EkipConfirmState = "pending" | "accepted" | "confirmed";

/**
 * Where one staff member stands on a shift, read off the confirms map.
 *
 * `acceptOnAssign` — dòng của đơn bật "được xếp là nhận ca" (quyết định 0234):
 * có tên trong ca là đã nhận, nên không bao giờ rơi về "pending".
 */
export function ekipConfirmState(
  confirms: EkipConfirmMap | undefined,
  staffId: string | null | undefined,
  acceptOnAssign = false,
): EkipConfirmState {
  if (confirms && staffId && confirms[staffId]) return "confirmed";
  if (acceptOnAssign && staffId) return "accepted";
  if (!confirms || !staffId) return "pending";
  if (confirms[staffId + ACCEPT_SUFFIX]) return "accepted";
  return "pending";
}

// ─── postponed_orders ────────────────────────────────────────────────────

export type PostponedEntry = {
  orderId: string;
  customer?: string;
  product?: string;
  branch?: string;
  /** ISO date the order was originally scheduled for */
  shootDate?: string | null;
  timeSlot?: string | null;
  /** Original order fields captured before the order was hidden from schedule. */
  originalShootDate?: string | null;
  originalStartTime?: string | null;
  originalEndTime?: string | null;
  originalStatus?: string | null;
  /** ISO datetime the postpone happened */
  postponedAt: string;
  reason?: string;
};

const EMPTY_POSTPONED: PostponedEntry[] = [];

export type PostponableOrder = {
  id: string;
  customer?: string;
  product?: string;
  branch?: string | null;
  shootDate?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  status?: string | null;
};

export function buildPostponedEntry(
  order: PostponableOrder,
  opts: { reason?: string; postponedAt?: string } = {},
): PostponedEntry {
  const entry: PostponedEntry = {
    orderId: order.id,
    branch: order.branch ?? "",
    shootDate: order.shootDate ?? null,
    timeSlot: order.startTime ?? null,
    originalShootDate: order.shootDate ?? null,
    originalStartTime: order.startTime ?? null,
    originalEndTime: order.endTime ?? null,
    originalStatus: order.status ?? null,
    postponedAt: opts.postponedAt ?? new Date().toISOString(),
    reason: opts.reason ?? "",
  };
  if (order.customer !== undefined) entry.customer = order.customer;
  if (order.product !== undefined) entry.product = order.product;
  return entry;
}

function hasOwn(obj: object, key: keyof PostponedEntry): boolean {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

export function hasOriginalSchedule(entry: PostponedEntry): boolean {
  return (
    hasOwn(entry, "originalShootDate") &&
    hasOwn(entry, "originalStartTime") &&
    hasOwn(entry, "originalEndTime") &&
    hasOwn(entry, "originalStatus") &&
    typeof entry.originalShootDate === "string" &&
    entry.originalShootDate.length > 0 &&
    typeof entry.originalStatus === "string" &&
    entry.originalStatus.length > 0
  );
}

export type ResumeOrderPatch = {
  shootDate: string;
  startTime?: string;
  endTime?: string;
};

export function buildResumeOrderPatch(entry: PostponedEntry): ResumeOrderPatch | null {
  if (!hasOriginalSchedule(entry)) return null;
  const patch: ResumeOrderPatch = { shootDate: entry.originalShootDate! };
  if (entry.originalStartTime) patch.startTime = entry.originalStartTime;
  if (entry.originalEndTime) patch.endTime = entry.originalEndTime;
  return patch;
}

export function usePostponedOrders() {
  return useQuery({
    queryKey: scheduleKeys.postponed,
    queryFn: async () => {
      const { data, response } = await api.GET("/schedule/postponed", {});
      if (response.status === 404 || !data) return EMPTY_POSTPONED;
      const v = (data as { value?: unknown }).value;
      return Array.isArray(v) ? (v as PostponedEntry[]) : EMPTY_POSTPONED;
    },
    staleTime: 30_000,
  });
}

export function useSavePostponedOrders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (next: PostponedEntry[]) => {
      const { error } = await api.PUT("/schedule/postponed", { body: { value: next } });
      if (error) throw new Error(serverErrorMessage(error, "Không lưu được danh sách hoãn"));
    },
    onSuccess: (_data, next) => {
      qc.setQueryData(scheduleKeys.postponed, next);
      void qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function usePostponeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string; reason?: string }) => {
      const { data, error } = await api.POST("/schedule/postponed/orders/{orderId}", {
        params: { path: { orderId } },
        body: { reason: reason ?? "" },
      });
      if (error || !data) {
        // Giữ nguyên câu máy chủ đã viết (cùng lý do usePatchOrder giải thích):
        // ca đã chụp xong bị từ chối hoãn bằng đúng câu "Ca đã chụp xong và đã
        // có link ảnh gốc…" — nuốt mất là nhân viên bấm lại mãi không hiểu.
        throw new Error(serverErrorMessage(error, "Không tạm hoãn được đơn"));
      }
      return data as PostponedEntry;
    },
    onSuccess: async (_data, vars) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: scheduleKeys.all }),
        qc.invalidateQueries({ queryKey: scheduleKeys.postponed }),
        qc.invalidateQueries({ queryKey: ["orders"] }),
        qc.invalidateQueries({ queryKey: ["orders", "detail", vars.orderId] }),
      ]);
    },
  });
}

/** Ô lịch mới cho đơn đang hoãn. Bỏ trống cả ba = về đúng ô cũ. */
export type ResumeSlot = {
  shootDate?: string;
  startTime?: string;
  endTime?: string;
};

/**
 * Đưa đơn đang hoãn trở lại lịch — về ô cũ, hoặc sang ô mới khi truyền slot.
 *
 * Nhận `string` (chỉ orderId) hoặc `{orderId, ...slot}`. Dạng chuỗi giữ nguyên
 * cho nút "Khôi phục" ở màn Lịch.
 *
 * KHÔNG thay được bằng PUT /orders/:id: tuyến đó ghi shoot_date nhưng không gỡ
 * đơn khỏi settings.postponed_orders, mà lưới lịch lọc theo đúng danh sách đó
 * — đơn nhận ngày mới rồi vẫn vô hình, không có lỗi nào để lần ra.
 */
export function useResumePostponedOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: string | ({ orderId: string } & ResumeSlot)) => {
      const { orderId, ...slot } = typeof vars === "string" ? { orderId: vars } : vars;
      const { data, error } = await api.POST("/schedule/postponed/orders/{orderId}/resume", {
        params: { path: { orderId } },
        ...(slot.shootDate ? { body: slot } : {}),
      });
      if (error || !data) {
        // Giữ nguyên câu máy chủ (cùng lý do usePostponeOrder giải thích): ngày
        // sai định dạng hay thiếu mốc gốc đều có câu riêng, nuốt mất là nhân
        // viên bấm lại mãi không hiểu.
        throw new Error(serverErrorMessage(error, "Không khôi phục được lịch"));
      }
      return data as PostponedEntry;
    },
    onSuccess: async (_data, vars) => {
      const orderId = typeof vars === "string" ? vars : vars.orderId;
      await Promise.all([
        qc.invalidateQueries({ queryKey: scheduleKeys.all }),
        qc.invalidateQueries({ queryKey: scheduleKeys.postponed }),
        qc.invalidateQueries({ queryKey: ["orders"] }),
        qc.invalidateQueries({ queryKey: ["orders", "detail", orderId] }),
      ]);
    },
  });
}

// ─── Số thợ chụp có ca (tô đậm khung giờ vượt ekip) ──────────────────────

export type PhotoCoverageRow = components["schemas"]["PhotoCoverageRow"];

const EMPTY_COVERAGE: PhotoCoverageRow[] = [];

/**
 * Ca ĐÃ DUYỆT của thợ chụp trong khoảng ngày đang hiện trên lưới.
 *
 * Không truyền branch dù lưới có bộ lọc chi nhánh: bộ lọc là ĐA CHỌN còn tuyến
 * chỉ nhận một cơ sở, nên lọc phía máy chủ sẽ hụt dữ liệu ngay khi người dùng
 * chọn hai cơ sở. BE đã gác phạm vi theo đúng luật của GET /schedule, và phép
 * đếm ở `overCapacity.ts` tự tách theo (chi nhánh, ngày).
 *
 * Lỗi mạng trả mảng rỗng thay vì ném: mất số thợ trực chỉ làm mất phần tô đậm,
 * không được phép làm sập cả trang Lịch.
 */
export function usePhotoCoverage({ from, to }: { from: string; to: string }) {
  return useQuery({
    queryKey: scheduleKeys.photoCoverage({ from, to }),
    queryFn: async () => {
      const { data, error } = await api.GET("/schedule/photo-coverage", {
        params: { query: { from, to } },
      });
      if (error || !data) return EMPTY_COVERAGE;
      return data;
    },
    enabled: !!from && !!to,
    staleTime: 60_000,
  });
}

// ─── schedule_closures (lịch đóng ca) ────────────────────────────────────

export type ScheduleClosure = components["schemas"]["ScheduleClosure"];
export type ScheduleClosureInput = components["schemas"]["ScheduleClosureInput"];

const EMPTY_CLOSURES: ScheduleClosure[] = [];

/** List closures for a date range. Pass a single branch to server-filter;
 *  all-branch closures (branch=null) are always included by the BE. */
export function useClosures({ from, to, branch }: ScheduleParams) {
  const enabled = !!from && !!to;
  return useQuery({
    queryKey: scheduleKeys.closureRange({ from, to, branch }),
    queryFn: async () => {
      const { data, error } = await api.GET("/schedule/closures", {
        params: { query: { from, to, ...(branch ? { branch } : {}) } },
      });
      if (error || !data) return EMPTY_CLOSURES;
      return data;
    },
    enabled,
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

export function useCreateClosure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ScheduleClosureInput) => {
      const { data, error } = await api.POST("/schedule/closures", { body: input });
      if (error || !data) {
        throw new Error(serverErrorMessage(error, "Không tạo được lịch đóng ca"));
      }
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: scheduleKeys.closures }),
  });
}

export function useDeleteClosure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE("/schedule/closures/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(serverErrorMessage(error, "Không xoá được lịch đóng ca"));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: scheduleKeys.closures }),
  });
}

// ─── staff (used by EkipPicker) ──────────────────────────────────────────

export function useStaffByDept(department?: string) {
  return useQuery({
    queryKey: ["staff", { department: department ?? null }],
    queryFn: async () => {
      const { data } = await api.GET("/staff", {
        params: { query: department ? { department } : {} },
      });
      return data ?? [];
    },
    staleTime: 60_000,
  });
}
